import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import RightModalLayout from '../../../../layouts/Modal/RightModalLayout';
import ModalSection from '../ModalSection';
import { SIDEBAR_ACTIONS } from '../../../../../constants/actionsConstants';
import PromptModel from '../../../../models/PromptModel';
import AgentModel from '../../../../models/AgentModel';
// import StatisticsSection from './StatisticsSection'; // TODO: Uncomment when statistics endpoint is ready
import agentsSeller from '../../../../../assets/images/chatbot_ai/agents_seller.svg';
import agentsSupport from '../../../../../assets/images/chatbot_ai/agents_support.svg';
import agentsPlanner from '../../../../../assets/images/chatbot_ai/agents_planner.svg';
import agentsCustom from '../../../../../assets/images/chatbot_ai/agents_custom.svg';
import agentsSellerMini from '../../../../../assets/images/chatbot_ai/agents_seller_mini.svg';
import agentsSupportMini from '../../../../../assets/images/chatbot_ai/agents_support_mini.svg';
import agentsPlannerMini from '../../../../../assets/images/chatbot_ai/agents_planner_mini.svg';
import agentsCustomMini from '../../../../../assets/images/chatbot_ai/agents_custom_mini.svg';

const AlertaMediumIcono = ({ t }) => {
  return (
    <div className="tw-bg-blue-50 tw-border tw-border-blue-100 tw-border-solid tw-flex tw-gap-1 tw-items-start tw-p-4 tw-relative tw-rounded-lg">
      <i style={{ color: '#198FCC', fontSize: '16px' }} className="mi-info-outline"></i>
      <p className="tw-flex-1 tw-font-medium tw-leading-4 tw-min-h-px tw-min-w-px tw-not-italic tw-text-blue-500 tw-text-xs tw-whitespace-pre-wrap">
        {t('agents.show.alert_message')}
      </p>
    </div>
  );
};

const AgentDetailModal = ({ agent, onClose }) => {
  const { t } = useTranslation('ChatbotIA');
  const dispatch = useDispatch();
  const [roleAndGoal, setRoleAndGoal] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [agentName, setAgentName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);

  const typeToTranslationKey = {
    sales: 'seller',
    support: 'support',
    appointments: 'planner',
    custom: 'custom'
  };

  const getAgentImage = (type) => {
    const images = {
      sales: agentsSeller,
      support: agentsSupport,
      appointments: agentsPlanner,
      custom: agentsCustom
    };
    return images[type] || agentsCustom;
  };

  const getAgentMiniIcon = (type) => {
    const icons = {
      sales: agentsSellerMini,
      support: agentsSupportMini,
      appointments: agentsPlannerMini,
      custom: agentsCustomMini
    };
    return icons[type] || agentsCustomMini;
  };

  useEffect(() => {
    if (agent?.id) {
      loadRoleAndGoalPrompt();
    }
  }, [agent?.id]);

  useEffect(() => {
    const nextName = agent?.name || '';
    setAgentName(nextName);
    setNameDraft(nextName);
    setIsEditingName(false);
    setIsSavingName(false);
  }, [agent?.id]);

  const loadRoleAndGoalPrompt = () => {
    if (!agent?.id) return;

    PromptModel.get(agent.id, { type: 'role_and_goal' })
      .then(response => {
        const prompts = response.data || [];
        const goalPrompt = prompts.find(p => p.type === 'role_and_goal');
        setRoleAndGoal(goalPrompt?.prompt || '');
      });
  };

  const truncateText = (text, maxLength = 250) => {
    if (!text) return '';
    const strippedText = text.replace(/<[^>]*>/g, '');
    if (strippedText.length <= maxLength) return strippedText;
    return strippedText.substring(0, maxLength) + '...';
  };

  const closeModal = () => {
    if (typeof onClose === 'function') onClose();
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  };

  const cancelNameEdit = () => {
    setNameDraft(agentName);
    setIsEditingName(false);
  };

  const saveName = () => {
    const trimmed = (nameDraft || '').trim();
    const current = (agentName || agent?.name || '').trim();

    if (!agent?.id || isSavingName) return;
    if (!trimmed) {
      showtoast(t('agents.create.name_required'), 'error');
      return;
    }
    if (trimmed === current) {
      setIsEditingName(false);
      return;
    }

    setIsSavingName(true);
    AgentModel.put({ id: agent.id, name: trimmed })
      .then(() => {
        setAgentName(trimmed);
        setNameDraft(trimmed);
        setIsEditingName(false);
        showtoast(t('agents.show.name_updated'), 'success');
      })
      .catch(() => {
        showtoast(t('agents.show.name_update_error'), 'error');
        setNameDraft(agentName);
      })
      .finally(() => {
        setIsSavingName(false);
      });
  };

  const createdAt = new Date(agent.created_at).toLocaleDateString(
    'es-ES',
    { day: 'numeric', month: 'long', year: 'numeric' }
  );

  const StatusBadge = ({ active, t }) => {
    const isActive = active;

    return (
      <div className={`tw-flex tw-gap-[2px] tw-items-center tw-px-[4px] tw-py-[2px] tw-rounded-[5px] ${isActive ? "tw-bg-[#effaf3]" : "tw-bg-[#fef3f2]"}`}>
        <i
          style={{ color: isActive ? '#1F7C4D' : '#E84B5F', fontSize: '12px' }}
          className={isActive ? 'mi-checkmark-circle-2-outline' : 'mi-close-circle-outline'}
        />
        <p className={`tw-text-[12px] tw-leading-[16px] ${isActive ? "tw-text-[#1f7c4d]" : "tw-text-[#E84B5F]"}`}>
          {isActive ? t('agents.show.active') : t('agents.show.inactive')}
        </p>
      </div>
    );
  };

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={t('agents.show.title')}
        submitAction={closeModal}
        submitLabel={t('agents.show.close_button')}
        closeAction={closeModal}
      >

        <div className="tw-bg-gradient-to-b tw-from-white tw-from-[55.1%] tw-to-[#EAFEFF] tw-border tw-border-[#85cdf4] tw-rounded-[12px] tw-px-[24px] tw-pt-[32px] tw-pb-[24px] tw-flex tw-flex-col tw-gap-[16px] tw-w-full">
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-[14px] tw-px-[85px]">

            <div className="tw-bg-[#0c2c45] tw-border-4 tw-border-white tw-rounded-full tw-shadow tw-size-[96px] tw-flex tw-items-center tw-justify-center">
              <img
                alt={agent?.name}
                src={getAgentImage(agent?.type)}
                className="tw-size-[95px] tw-object-cover"
              />
            </div>

            {!isEditingName ? (
              <div className="tw-flex tw-items-center">
                <p className="tw-text-[22px] tw-font-semibold tw-text-[#101828]">
                  {agentName || agent?.name}
                </p>
                <button
                  type="button"
                  className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-md tw-p-1 tw-text-[#667085] hover:tw-bg-white/70 hover:tw-text-[#344054] tw-transition-colors disabled:tw-opacity-50"
                  onClick={() => setIsEditingName(true)}
                  disabled={!agent?.id}
                  aria-label={t('agents.show.edit_name')}
                >
                  <i className="mi-edit-outline tw-text-[18px]" />
                </button>
              </div>
            ) : (
              <div className="tw-flex tw-items-center tw-w-full">
                <input
                  className="tw-flex-1 tw-min-w-0 tw-rounded-lg tw-border tw-border-[#D0D5DD] tw-bg-white tw-px-3 tw-py-2 tw-text-[16px] tw-font-semibold tw-text-[#101828] focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#85cdf4]"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveName();
                    if (e.key === 'Escape') cancelNameEdit();
                  }}
                  autoFocus
                  disabled={isSavingName}
                />
                <button
                  type="button"
                  className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-md tw-ph-1 tw-ml-1 tw-text-[#1F7C4D] hover:tw-bg-white/70 tw-transition-colors disabled:tw-opacity-50"
                  onClick={saveName}
                  disabled={isSavingName}
                  aria-label={t('agents.show.save_name')}
                >
                  <i className="mi-checkmark-outline tw-text-[18px]" />
                </button>
                <button
                  type="button"
                  className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-md tw-ph-1 tw-ml-1 tw-text-[#E84B5F] hover:tw-bg-white/70 tw-transition-colors disabled:tw-opacity-50"
                  onClick={cancelNameEdit}
                  disabled={isSavingName}
                  aria-label={t('agents.show.cancel_edit_name')}
                >
                  <i className="mi-close-outline tw-text-[18px]" />
                </button>
              </div>
            )}

            <div className="tw-flex tw-gap-[6px]">
              <div className="tw-bg-[#f1f9fe] tw-flex tw-gap-[2px] tw-items-center tw-px-[4px] tw-py-[2px] tw-rounded-[5px]">
                <img src={getAgentMiniIcon(agent?.type)} className="tw-w-[12px] tw-h-[12px]" />
                <p className="tw-text-[#198fcc] tw-text-[12px]">
                  {t(`agents.types.${typeToTranslationKey[agent.type]}.subtitle`)}
                </p>
              </div>

              <StatusBadge active={agent?.active} t={t} />
            </div>
          </div>

          <div className="tw-border-t tw-border-[#d6dce1] tw-pt-[16px] tw-flex tw-justify-between">
            <p className="tw-text-[#45535f] tw-text-[12px]">
              {t('agents.show.created_at')}: {createdAt}
            </p>

            <p className="tw-text-[#45535f] tw-text-[12px]">
              {t('agents.show.created_by')}: {agent?.created_by?.full_name}
            </p>
          </div>
        </div>

        <ModalSection
          title={t('agents.show.business_type_title')}
          description={agent?.business_model}
        />

        <ModalSection
          title={t('agents.show.goal_title')}
          description={truncateText(roleAndGoal)}
        />

        {/* TODO: Uncomment when statistics endpoint is ready */}
        {/* <ModalSection
          title={t('agents.show.statistics_title')}
          afterContent={<StatisticsSection showTitle={false} />}
        /> */}

        <AlertaMediumIcono t={t} />
      </RightModalLayout>
    </div>
  );
};

export default AgentDetailModal;
